import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  OnDestroy,
  OnInit,
  SkipSelf,
} from '@angular/core';
import { EfileAddEditComponent } from '../add-edit/add-edit.component';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  buildHeaderDataShell,
} from '../../../shared/workspace-element-context';

/**
 * Tab key set rendered in the parent `data.html` toolbar
 * (legacy `submissions-dasboard.submission.data` ui-router state —
 * see `ui/app/components/efile/efile.js` L130). Each key maps to a
 * sibling sub-state that legacy ui-router would swap into the nested
 * `<ui-view>` underneath `data.html`'s tab strip.
 */
export type ElementsDataTabKey = 'data' | 'pdf' | 'overrides' | 'xml';

interface ElementsDataTab {
  key: ElementsDataTabKey;
  label: string;
  /**
   * Canonical sub-state pushed into `submission-data.component.ts`
   * `activeSubState` so deep-links + breadcrumbs keep working. Mirrors the
   * legacy `submissions-dasboard.submission.<state>` keys defined in
   * `ui/app/components/efile/efile.js`.
   */
  subState: string;
  /**
   * Path segments after `/submissions-dasboard/submission/:id/`. The
   * `data.addEdit` legacy nested state (efile.js L149) keeps both
   * segments (`['data', 'add-edit']`) so the breadcrumb still renders
   * "Data > Add / Edit" exactly like ui-router did.
   */
  urlSegments: string[];
}

/**
 * Port of legacy `efile/efileElements/data.html` tab strip, now used as a
 * focused shell for Add/Edit flow only.
 *
 * Nested inside `gtw-efile-add-edit` (entity aside is a sibling of this host).
 * Tab clicks emit sibling sub-state intents (PDF/Overrides/XML); form body is
 * projected into `<ng-content>` (tiles + editor from add-edit).
 */
@Component({
    selector: 'gtw-efile-elements-data',
    templateUrl: './efile-elements-data.component.html',
    styleUrls: ['./efile-elements-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileElementsDataComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  /**
   * Bubbled to `submission-detail` (which re-emits via its existing
   * `(subStateChange)` Output). The host (`submission-data`) already
   * accepts `{subState, urlSegments}` payloads, so toolbar clicks reuse
   * the same Router.navigate path that sidebar clicks do.
   */
  @Output() tabChange = new EventEmitter<{ subState: string; urlSegments: string[] }>();

  /**
   * Pre-selected tab (currently expected as `'data'` because this shell is
   * mounted from Add/Edit states).
   */
  @Input() activeTab: ElementsDataTabKey = 'data';

  private isInitialized = false;
  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  rowDataKeys: string[] = [];

  /**
   * Tab strip — order matches legacy `data.html` left-to-right
   * (Data is the primary entry point so it sits leftmost; XML is the
   * least-used and floats to the right).
   */
  readonly tabs: ElementsDataTab[] = [
    { key: 'data',      label: 'Data',      subState: 'add-edit',   urlSegments: ['data', 'add-edit'] },
    { key: 'pdf',       label: 'PDF',       subState: 'pdf-upload', urlSegments: ['pdf-upload'] },
    { key: 'overrides', label: 'Overrides', subState: 'overrides',  urlSegments: ['overrides'] },
    { key: 'xml',       label: 'XML',       subState: 'xml-upload', urlSegments: ['xml-upload'] },
  ];

  /**
   * Safety guard: if this shell is ever reused in XML context, hide the
   * legacy Data tab there.
   */
  get visibleTabs(): ElementsDataTab[] {
    if (this.activeTab === 'xml') {
      return this.tabs.filter((tab) => tab.key !== 'data');
    }
    return this.tabs;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    /** Parent add-edit when this shell is nested in its template (SkipSelf skips this host). */
    @Optional() @SkipSelf() private addEdit?: EfileAddEditComponent,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.filterParams = normalizeFilterParams(activeScreen);
    if (!this.isInitialized) {
      this.isInitialized = true;
      this.rowDataKeys =
        this.rowData && typeof this.rowData === 'object' && !Array.isArray(this.rowData)
          ? Object.keys(this.rowData)
          : [];
    }
    this.cdr.markForCheck();
  }

  /**
   * Toolbar click handler — no-ops when the user clicks the already-active
   * tab and otherwise emits a structured payload that
   * `submission-data.handleSubStateChange` knows how to turn into a
   * Router.navigate call (preserving the legacy nested URL for the Data
   * tab via `['data', 'add-edit']`).
   */
  selectTab(tab: ElementsDataTab): void {
    if (tab.key === this.activeTab) return;
    this.tabChange.emit({ subState: tab.subState, urlSegments: tab.urlSegments });
  }

  /**
   * Legacy `DataController.expandView` → `$broadcast('efile:expand-view')`
   * (EfileElementsController.js L1118, AddEditController L2134–2136).
   * Collapses the DATA form-tile strip so the editor (or PDF sibling route
   * content pattern) can use full width.
   */
  expandView(): void {
    this.addEdit?.toggleExpandView();
    this.cdr.markForCheck();
  }
}
